// Verbatim install / token-exchange snippets the guides render. Data, not UI.

// ── Terramantle CLI (shared snippets + facts) ─────────────────────────────────
export const CLI_REPO = 'https://github.com/terramantle/terramantle-cli'

export const CLI_INSTALL_SH = `curl --proto '=https' --tlsv1.2 -LsSf \\
  ${CLI_REPO}/releases/latest/download/cli-installer.sh | sh`

/** The install step for a GitHub Actions job (8-space `steps:` indentation). */
export const CLI_INSTALL_GITHUB = `      - name: Install the Terramantle CLI
        run: |
          curl --proto '=https' --tlsv1.2 -LsSf \\
            ${CLI_REPO}/releases/latest/download/cli-installer.sh | sh`

/** The install step for a GitLab CI `script:` block. */
export const CLI_INSTALL_GITLAB = `    - |
      curl --proto '=https' --tlsv1.2 -LsSf \\
        ${CLI_REPO}/releases/latest/download/cli-installer.sh | sh`

/** GitLab `before_script` that installs curl, the CLI and tofu on a plain Ubuntu
 *  image. The CLI's release binaries are glibc, so an Alpine-based image (e.g.
 *  hashicorp/terraform) cannot run them - hence ubuntu + an explicit tofu install. */
export const CLI_GITLAB_SETUP = `  before_script:
    - apt-get update && apt-get install -y curl unzip
${CLI_INSTALL_GITLAB}
    - curl -fsSL https://get.opentofu.org/install-opentofu.sh | sh -s -- --install-method standalone`

/** `id_tokens:` block - GitLab has no ambient OIDC token, it must be requested. */
export const CLI_GITLAB_ID_TOKENS = `  id_tokens:
    TERRAMANTLE_ID_TOKEN:
      aud: https://registry.terramantle.dev`

// ── Token exchange snippets (shared) ──────────────────────────────────────────

// Authentik's OIDC token endpoint. Distinct from VITE_OIDC_ISSUER (the SPA's per-app
// issuer URL) - the token grant is served from the application-scoped /o/token/ path.
// A build-time override is supported for non-production tenants.
export const OIDC_TOKEN_ENDPOINT =
  (import.meta.env['VITE_OIDC_TOKEN_ENDPOINT'] as string) ||
  'https://sso.terramantle.dev/application/o/token/'

// Fixed confidential provider client_id (the azp) shared by all bot tokens. This
// is not a secret - the per-token username + app-password are the credential.
export const BOT_PROVIDER_CLIENT_ID = 'terramantle-bots'

export const TOKEN_EXCHANGE_CLI = `# Exchange bot-token credentials for an access token
TOKEN=$(curl -s -X POST \\
  ${OIDC_TOKEN_ENDPOINT} \\
  -H "Content-Type: application/x-www-form-urlencoded" \\
  --data-urlencode "grant_type=client_credentials" \\
  --data-urlencode "client_id=${BOT_PROVIDER_CLIENT_ID}" \\
  --data-urlencode "username=<bot token client id>" \\
  --data-urlencode "password=<bot token client secret>" \\
  --data-urlencode "scope=openid terramantle" | jq -r .access_token)`

export const TOKEN_EXCHANGE_GITHUB = `      - name: Get bot token
        id: token
        env:
          TM_CLIENT_ID:     \${{ secrets.TM_CLIENT_ID }}
          TM_CLIENT_SECRET: \${{ secrets.TM_CLIENT_SECRET }}
        run: |
          TOKEN=$(curl -s -X POST ${OIDC_TOKEN_ENDPOINT} \\
            -H "Content-Type: application/x-www-form-urlencoded" \\
            --data-urlencode "grant_type=client_credentials" \\
            --data-urlencode "client_id=${BOT_PROVIDER_CLIENT_ID}" \\
            --data-urlencode "username=$TM_CLIENT_ID" \\
            --data-urlencode "password=$TM_CLIENT_SECRET" \\
            --data-urlencode "scope=openid terramantle" \\
            | jq -r .access_token)
          echo "token=$TOKEN" >> $GITHUB_OUTPUT`

export const TOKEN_EXCHANGE_GITLAB = `  # Exchange bot-token credentials for an access token
  - TOKEN=$(curl -s -X POST ${OIDC_TOKEN_ENDPOINT}
      -H "Content-Type: application/x-www-form-urlencoded"
      --data-urlencode "grant_type=client_credentials"
      --data-urlencode "client_id=${BOT_PROVIDER_CLIENT_ID}"
      --data-urlencode "username=$TM_CLIENT_ID"
      --data-urlencode "password=$TM_CLIENT_SECRET"
      --data-urlencode "scope=openid terramantle"
      | jq -r .access_token)`


// ── Shared per-tab footnotes ──────────────────────────────────────────────────

/** Shown under the `curl` tab: the raw API needs a stored bot credential. */

// ── Terramantle CLI: Commands ────────────────────────────────────────────────
export const CLI_COMMAND_TREE = `terramantle
├── init                        # scaffold a repo (manifest + CI + skeleton)
├── upgrade                     # diff desired scaffold vs disk, apply the delta
├── providers
│   ├── ls [--at-risk]          # providers in use (usage rollup + TRUST)
│   └── show <ns>/<type>        # versions + trust + used-by workspaces
├── modules
│   ├── search <query>          # registry search
│   ├── show <ns>/<name>/<provider>
│   ├── changed [--all]         # changed modules + proposed next semver
│   └── publish [--all] [...]   # package · docs · hash · upload
├── lock
│   └── push [path]             # upload .terraform.lock.hcl (default ./)
├── state
│   ├── ls                      # workspaces in the org
│   ├── versions <workspace>    # version history (serial, actor, pushed_at)
│   ├── promote <workspace> <versionId>       # restore a version to latest
│   ├── rollback <workspace> [--to <serial>]  # promote previous (or --to) serial
│   ├── unlock <workspace>      # force-unlock
│   └── publish [--all] [...]   # per-workspace lock-file upload + posture
├── repo
│   └── tag [--dry-run]         # mono: push <module>@<version> release tags
├── auth
│   ├── login · logout · whoami
│   ├── token                   # print the bearer to stdout
│   └── env [--terraform]       # shell exports for eval
├── console [path]              # open the portal in your browser
├── context
│   └── ls · current · use <name> · set <name> [--org o] [--workspace w]
├── config
│   └── view                    # effective resolved config (secrets redacted)
├── completion <shell>          # bash | zsh | fish
└── version`

